"use client";

import { useMemo } from "react";
import { Typography } from "./typography";
import { cn, formatPercentage } from "@drpcrd/utils";

type HorizontalBarChartData = {
  label: string;
  value: number;
};

type Props = {
  data: HorizontalBarChartData[];
  title?: string;
  valueFormatter?: (value: number) => string;
  className?: string;
  showPercentage?: boolean;
  barHeight?: "sm" | "md" | "lg";
  defaultColor?: string;
  totalValue?: number;
  /** Text size of the labels and values. */
  labelSize?: "sm" | "base";
};

/**
 * A simple, custom horizontal bar chart component that displays data proportionally
 * using only Tailwind CSS classes
 */
export const HorizontalBarChart = ({
  data,
  valueFormatter,
  className,
  showPercentage = true,
  barHeight = "sm",
  totalValue,
  labelSize = "base",
}: Props) => {
  // Define the rotating color pattern
  const colorClasses = ["bg-primary", "bg-neutral-300", "bg-neutral-600"];

  // Calculate the total value for proportional display
  const sum = totalValue ?? data.reduce((acc, item) => acc + item.value, 0);

  // Get height class based on barHeight prop
  const heightClass = useMemo(() => {
    switch (barHeight) {
      case "sm":
        return "h-6";
      case "lg":
        return "h-12";
      case "md":
      default:
        return "h-10";
    }
  }, [barHeight]);

  // Format value consistent with other components
  const formatValue = (value: number): string => {
    if (valueFormatter) {
      return valueFormatter(value);
    } else if (showPercentage) {
      return formatPercentage(value, 1);
    } else {
      return value.toString();
    }
  };

  // Calculate percentages for each item
  const itemsWithPercentage = useMemo(
    () =>
      data.map((item, index) => {
        const percentage = sum > 0 ? item.value / sum : 0;
        const widthInPercent: string = `${(percentage * 100).toFixed(0)}%`;

        // Assign colors in a repeating pattern
        const colorIndex = index % colorClasses.length;
        const bgClass = colorClasses[colorIndex];

        return {
          ...item,
          originalValue: item.value,
          value: percentage,
          widthInPercent,
          bgClass,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, sum],
  );

  return (
    <div className={cn("w-full", className)}>
      {/* Horizontal bar chart */}
      <div className={`flex w-full ${heightClass}`}>
        {itemsWithPercentage.map((item, index) => (
          <div
            key={index}
            className={`${item.bgClass}`}
            style={{ width: item.widthInPercent }}
            aria-label={`${item.label}: ${formatValue(item.value)}`}
            role="img"
          />
        ))}
      </div>

      {/* Labels and percentages */}
      <div className="mt-1 flex">
        {itemsWithPercentage.map((item, index) => (
          <div
            key={index}
            className={cn(
              "flex flex-col",
              index === 0
                ? "items-start"
                : index === itemsWithPercentage.length - 1
                  ? "items-end"
                  : "items-center",
            )}
            style={{ width: item.widthInPercent }}
          >
            <Typography size={labelSize} weight="semibold">
              {item.label}
            </Typography>
            {showPercentage && (
              <Typography size={labelSize} weight="normal">
                {formatValue(item.value)}
              </Typography>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
